import { useCallback, useEffect, useRef, useState, MouseEvent } from 'react';
import { connect, useDispatch } from 'react-redux';
import cn from 'classnames';

import { ITableColumn } from 'CorePackage/TableModule/interfaces/ITableColumn';
import { ISystemInoperability } from 'ApiPackage/SystemInoperabilityModule/interfaces/ISystemInoperability';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_ISO_FNS, DATE_FORMAT_LONG_MINUTE_FNS } from 'CorePackage/MainModule/constants/dateFormat';
import { getCoreIsLoading } from 'CorePackage/MainModule/selectors/coreSelectors';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { handleSortChange } from 'CorePackage/MainModule/common/handleSortChange';
import { getSortOrderBySelected } from 'CorePackage/MainModule/common/getSortOrderBySelected';
import { sortMapper } from 'CorePackage/MainModule/common/getSortOrder';
import { getSortIcon } from 'CorePackage/MainModule/common/getSortIcon';
import { Button } from 'UIPackage/MainModule/components/Button';

import tableStyles from './SystemInoperabilityContainer.module.scss';
import { initialSystemInoperabilityTableColumns } from '../constants/initialSystemInoperabilityTableColumns';
import { getList, getListPage, getTotalCount } from '../selectors/systemInoperabilitySelectors';
import {
  adminGetSystemInoperabilityListNextPage,
  getSystemInoperabilityList,
  setSystemInoperabilityListOrder,
} from '../actions/systemInoperabilityAction';

interface ISystemInoperabilityStoreProps {
  systemInoperabilityList: ISystemInoperability[];
  totalCount: number;
  loading: boolean;
  page: number;
}

const SystemInoperabilityContainerWithoutStore = ({
  systemInoperabilityList,
  totalCount,
  loading,
  page,
}: ISystemInoperabilityStoreProps) => {
  const dispatch = useDispatch();
  const theadDateCellClassName = cn(tableStyles.Cell, tableStyles.DateCell, tableStyles.TheadCell);
  const theadReasonCellClassName = cn(tableStyles.Cell, tableStyles.ReasonCell, tableStyles.TheadCell);
  const tbodyRowClassName = cn(tableStyles.Row, tableStyles.TbodyRow);
  const tbodyDateCellClassName = cn(tableStyles.Cell, tableStyles.DateCell, tableStyles.TbodyCell);
  const tbodyReasonCellClassName = cn(tableStyles.Cell, tableStyles.ReasonCell, tableStyles.TbodyCell);
  const [tableColumns, setTableColumns] = useState<ITableColumn<ISystemInoperability>[]>(
    initialSystemInoperabilityTableColumns,
  );
  const onLoadMore = useCallback(() => dispatch(adminGetSystemInoperabilityListNextPage()), [dispatch]);
  const table = useRef<HTMLTableElement>(null);
  const isSortAvailable = useRef(true);

  useEffect(() => {
    dispatch(getSystemInoperabilityList());
  }, [dispatch]);

  const handleSortClick = useCallback(
    (e: MouseEvent<HTMLElement>): void => {
      if (isSortAvailable.current) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { id } = (e.target as any).dataset;
        const newColumnsData = handleSortChange(tableColumns, id as keyof ISystemInoperability);

        setTableColumns(newColumnsData);
        const newOrder = getSortOrderBySelected(newColumnsData, sortMapper);

        dispatch(setSystemInoperabilityListOrder(newOrder));
        dispatch(getSystemInoperabilityList());
      }
    },
    [dispatch, tableColumns],
  );

  useEffect(() => {
    if (page <= 0) {
      return;
    }
    setTimeout(() => {
      table.current?.scrollIntoView(false);
    }, 0);
  }, [page]);

  return (
    <div className={tableStyles.SystemInoperabilityPage}>
      <div className={tableStyles.TableWrapper}>
        <div className={tableStyles.Header}>
          <h1 className={tableStyles.Title}>Периоды неработоспособности системы</h1>
        </div>
        <table className={tableStyles.Table} ref={table}>
          <thead className={tableStyles.TableHead}>
            <tr className={tableStyles.Row}>
              {tableColumns.map((row, i) => (
                <th
                  onClick={handleSortClick}
                  data-id={row.id}
                  className={i < 2 ? theadDateCellClassName : theadReasonCellClassName}
                  key={i}
                >
                  {row.name}
                  {getSortIcon(tableColumns, row.id)}
                </th>
              ))}
            </tr>
          </thead>
          {!loading && (
            <tbody>
              {systemInoperabilityList.map((row, i) => (
                <tr className={tbodyRowClassName} key={i}>
                  <td className={tbodyDateCellClassName}>
                    {formatDate({
                      date: row.startDate ?? '',
                      dateFormat: DATE_FORMAT_LONG_MINUTE_FNS,
                      parseFormat: DATE_FORMAT_ISO_FNS,
                    })}
                  </td>
                  <td className={tbodyDateCellClassName}>
                    {formatDate({
                      date: row.endDate ?? '',
                      dateFormat: DATE_FORMAT_LONG_MINUTE_FNS,
                      parseFormat: DATE_FORMAT_ISO_FNS,
                    })}
                  </td>
                  <td className={tbodyReasonCellClassName}>{row.reason}</td>
                </tr>
              ))}
            </tbody>
          )}
        </table>
        {systemInoperabilityList.length < totalCount && (
          <Button
            className={tableStyles.OpenDataButton}
            type="button"
            text="Показать еще"
            color="load"
            onClick={onLoadMore}
          />
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: IRootState) => ({
  systemInoperabilityList: getList(state),
  totalCount: getTotalCount(state),
  loading: getCoreIsLoading(state),
  page: getListPage(state),
});

export const SystemInoperabilityContainer = connect(mapStateToProps)(SystemInoperabilityContainerWithoutStore);
